/**
 * Suppresses a known-benign runtime error emitted by Google reCAPTCHA when
 * Firebase's `RecaptchaVerifier.clear()` doesn't fully unregister its
 * background timers.
 *
 * Symptom:  "Invalid reCAPTCHA client id: <hash>" thrown from
 *           `www.gstatic.com/recaptcha/.../recaptcha__en.js` after phone-OTP
 *           verification finishes and we tear down the invisible reCAPTCHA
 *           widget. The error is thrown asynchronously (setTimeout inside
 *           recaptcha__en.js), so try/catch at the call site cannot catch it.
 *           In production the browser silently swallows it; in dev CRA's
 *           `react-error-overlay` covers the whole app with a red modal.
 *
 * Fix:      Install listeners on `window`'s error / unhandledrejection events
 *           at CAPTURE phase (registered as early as possible via import in
 *           index.js) that call `stopImmediatePropagation` + `preventDefault`
 *           on THIS SPECIFIC message. We also override `window.onerror` and
 *           patch `console.error` to filter the same string — belt and
 *           braces, because react-error-overlay hooks in via multiple paths.
 *
 * We deliberately match ONLY the exact reCAPTCHA-client-id noise — every
 * other error still surfaces normally.
 */

const RECAPTCHA_NOISE = /Invalid reCAPTCHA client id/i;

function isRecaptchaNoise(input) {
  if (!input) return false;
  const s = typeof input === "string" ? input : input.message || String(input);
  return RECAPTCHA_NOISE.test(s);
}

if (typeof window !== "undefined" && !window.__psg_recaptcha_suppressor_installed) {
  window.__psg_recaptcha_suppressor_installed = true;

  // 1) Capture-phase `error` listener — swallow the ErrorEvent before any
  //    other listener (including react-error-overlay) can see it.
  window.addEventListener(
    "error",
    (ev) => {
      const msg = ev?.message || ev?.error?.message || "";
      if (isRecaptchaNoise(msg)) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
        return false;
      }
      return undefined;
    },
    true,
  );

  // 2) Same for unhandled promise rejections.
  window.addEventListener(
    "unhandledrejection",
    (ev) => {
      if (isRecaptchaNoise(ev?.reason)) {
        ev.stopImmediatePropagation();
        ev.preventDefault();
      }
    },
    true,
  );

  // 3) Override `window.onerror` — react-error-overlay hooks in this way, and
  //    we want to run BEFORE it. We chain to the previous handler for every
  //    other error so we don't stomp on other consumers.
  const prevOnError = window.onerror;
  window.onerror = function psgOnErrorFilter(message, source, lineno, colno, error) {
    if (isRecaptchaNoise(message) || isRecaptchaNoise(error)) {
      // Returning true tells the browser we've handled the error → default
      // (console + subsequent listeners) is suppressed.
      return true;
    }
    if (typeof prevOnError === "function") {
      return prevOnError.call(this, message, source, lineno, colno, error);
    }
    return false;
  };

  // 4) Filter noisy console output too — react-error-overlay reads from
  //    `console.error`, so dropping this specific string keeps the overlay
  //    quiet even if the browser somehow re-emits the message.
  const origConsoleError = console.error.bind(console);
  console.error = function psgConsoleErrorFilter(...args) {
    for (const a of args) {
      if (isRecaptchaNoise(a)) return;
    }
    origConsoleError(...args);
  };

  // 5) Last-resort belt: CRA / webpack-dev-server can still inject an error
  //    overlay via a hidden iframe or a full-page DIV. Watch the DOM and, if
  //    an overlay appears with ONLY the reCAPTCHA-noise message inside, hide
  //    it. We intentionally leave the overlay visible if it contains any
  //    other error text — this preserves legitimate crash reports for devs.
  const looksLikeErrorOverlay = (node) => {
    if (!(node instanceof HTMLElement)) return false;
    // CRA's overlay renders as an iframe with these attrs OR as a raw div
    if (node.tagName === "IFRAME" && node.id && /webpack|react-refresh|overlay/i.test(node.id)) return true;
    if (node.tagName === "IFRAME" && node.title && /error/i.test(node.title)) return true;
    // webpack-dev-server 4+ full-screen overlay
    if (node.matches && node.matches('[data-webpack-dev-server-client-overlay]')) return true;
    return false;
  };

  const tryHideMatchingOverlay = (overlayNode) => {
    // Give the overlay a tick to populate its shadow/iframe content.
    setTimeout(() => {
      try {
        const doc =
          overlayNode.contentDocument ||
          (overlayNode.contentWindow && overlayNode.contentWindow.document) ||
          null;
        const text = doc ? doc.body?.innerText || "" : overlayNode.innerText || "";
        if (!text) return;
        // If the overlay ONLY contains our noise (no other error paragraphs),
        // hide it entirely. Otherwise leave it alone.
        const otherErrorish = text
          .split(/\n{2,}/)
          .some((chunk) => /error|exception|typeerror|referenceerror/i.test(chunk) && !RECAPTCHA_NOISE.test(chunk));
        if (RECAPTCHA_NOISE.test(text) && !otherErrorish) {
          overlayNode.style.display = "none";
        }
      } catch (_e) {
        /* cross-origin / no iframe body — ignore */
      }
    }, 50);
  };

  if (typeof MutationObserver !== "undefined") {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (looksLikeErrorOverlay(node)) tryHideMatchingOverlay(node);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
}

export {};
